import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Clock, MapPin, MessageCircleHeart } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { InquiryForm } from '@/components/contact/inquiry-form'
import { siteConfig } from '@/constants/site'

const NEXT_STEPS = [
    {
        title: "We read every word",
        description: "Your inquiry goes straight to a senior planner, not a sales team."
    },
    {
        title: "We reply within two working days",
        description: "With a few questions and times for a free 45-minute consultation."
    },
    {
        title: "You receive a proposal",
        description: "Ideas, a budget outline and our planning fee in writing, usually within a week of the call."
    }
]

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Contact"
                description="Tell us about your wedding and book a free consultation with our Indian wedding planners. We reply within two working days."
            />

            <section id='inquiry' aria-labelledby='contact-title' className='relative overflow-hidden'>
                <div aria-hidden='true' className='pattern-jaali absolute inset-0 text-gold/[0.06]' />
                <div aria-hidden='true' className='absolute -left-40 top-20 size-[34rem] rounded-full bg-marigold/15 blur-3xl' />
                <div aria-hidden='true' className='toran absolute inset-x-0 top-0' />

                <div className='relative mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-20 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:pb-32 lg:pt-28'>
                    <div>
                        <p className='inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-gold'>
                            <span aria-hidden='true' className='h-px w-8 bg-current' />
                            Free consultation
                        </p>

                        <h1 id='contact-title' className='mt-5 text-5xl leading-[1.02] text-foreground sm:text-6xl'>
                            Tell us about <em className='text-primary'>your wedding</em>
                        </h1>

                        <p className='mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground'>
                            Whether you have a date, a venue and a guest list, or just a feeling about how it should look, this is where it starts.
                        </p>

                        <h2 className='mt-12 text-2xl text-foreground'>
                            What happens next
                        </h2>

                        <ol className='mt-6 space-y-6'>
                            {NEXT_STEPS.map((step, index) => (
                                <li key={step.title} className='flex gap-4'>
                                    <span aria-hidden='true' className='flex size-10 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-card font-heading text-xl text-primary'>
                                        {index + 1}
                                    </span>

                                    <div>
                                        <p className='font-medium text-foreground'>
                                            {step.title}
                                        </p>

                                        <p className='mt-1 leading-relaxed text-muted-foreground'>
                                            {step.description}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <ul className='mt-12 space-y-3 border-t border-border pt-8 text-sm text-foreground/85'>
                            <li className='flex gap-3'>
                                <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-gold' />
                                {siteConfig.studio}
                            </li>

                            <li className='flex gap-3'>
                                <Clock aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-gold' />
                                {siteConfig.hours}
                            </li>

                            <li className='flex gap-3'>
                                <MessageCircleHeart aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-gold' />
                                Consultations in English, Hindi, Punjabi and Gujarati
                            </li>
                        </ul>

                        <div className='relative mt-12 hidden max-w-xs lg:block'>
                            <div aria-hidden='true' className='arch-frame absolute -inset-2.5 border border-gold/40' />

                            <div className='arch-frame relative aspect-[4/5] overflow-hidden'>
                                <Image
                                    src='/images/couple.jpg'
                                    alt="A couple in linen kurtas walking together through a bright gallery"
                                    fill
                                    sizes='20rem'
                                    className='object-cover object-[50%_40%]'
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <InquiryForm />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default ContactPage
